import { useState, useEffect } from 'react'
import { useAuthStore } from '../stores/authStore'
import './AuthPanel.css'

interface AuthPanelProps {
  isOpen?: boolean
  onClose?: () => void
}

export function AuthPanel({ isOpen: controlledIsOpen, onClose }: AuthPanelProps = {}) {
  const {
    user,
    loginStep,
    tempEmail,
    isLoading,
    error,
    isLoginPanelOpen,
    sendOTP,
    verifyOTP,
    clearError,
    restoreSession,
    closeLoginPanel,
  } = useAuthStore()

  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [isRestoring, setIsRestoring] = useState(true)

  // 恢复会话
  useEffect(() => {
    const init = async () => {
      await restoreSession()
      setIsRestoring(false)
    }
    init()
  }, [restoreSession])

  // 处理发送 OTP
  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || isLoading) return

    clearError()
    const success = await sendOTP(email)
    if (success) {
      setOtp('')
    }
  }

  // 处理验证 OTP
  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!otp || otp.length !== 6 || isLoading) return

    clearError()
    const success = await verifyOTP(tempEmail, otp)
    if (success) {
      setOtp('')
      setEmail('')
    }
  }

  // 处理返回
  const handleBack = () => {
    useAuthStore.setState({ loginStep: 'email', error: null })
    setOtp('')
  }

  // 恢复会话中
  if (isRestoring) {
    return null
  }

  // 已登录 - 不再显示独立面板，用户信息在 TopBar 中显示
  if (user) {
    return null
  }

  // 判断是否显示登录面板（支持受控和非受控模式）
  const shouldShow = controlledIsOpen !== undefined ? controlledIsOpen : isLoginPanelOpen
  if (!shouldShow) {
    return null
  }

  const handleClose = () => {
    if (onClose) {
      onClose()
    } else {
      closeLoginPanel()
    }
  }

  // 未登录 - 显示登录面板
  return (
    <div className="auth-overlay">
      <div className="auth-panel">
        {/* 角落装饰 */}
        <div className="corner-decoration top-left"></div>
        <div className="corner-decoration top-right"></div>
        <div className="corner-decoration bottom-left"></div>
        <div className="corner-decoration bottom-right"></div>
        
        {/* 关闭按钮 */}
        <button
          type="button"
          className="auth-close-btn"
          onClick={handleClose}
          aria-label="关闭"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        
        {loginStep === 'email' && (
          <>
            <h2 className="auth-title">华夏志</h2>
            <p className="auth-subtitle">登录以保存游戏进度</p>

            <form className="auth-form" onSubmit={handleSendOTP}>
              <div className="auth-input-group">
                <label className="auth-label">邮箱地址</label>
                <div className="auth-input-wrapper">
                  <input
                    type="email"
                    className="auth-input"
                    placeholder="请输入邮箱"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>
              </div>

              {error && <div className="auth-error">{error}</div>}

              <button
                type="submit"
                className="auth-button auth-button-primary"
                disabled={isLoading || !email}
              >
                {isLoading ? (
                  <>
                    <span className="auth-loading"></span>
                    发送中...
                  </>
                ) : (
                  '发送验证码'
                )}
              </button>
            </form>

            <p className="auth-hint">
              首次使用将自动创建账号
              <br />
              验证码将发送至您的邮箱
            </p>
          </>
        )}

        {loginStep === 'otp' && (
          <>
            <h2 className="auth-title">验证邮箱</h2>
            <p className="auth-subtitle">验证码已发送至 {tempEmail}</p>

            <form className="auth-form" onSubmit={handleVerifyOTP}>
              <div className="auth-input-group">
                <label className="auth-label">验证码</label>
                <div className="auth-input-wrapper">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    className="auth-input otp-input"
                    placeholder="000000"
                    value={otp}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                      setOtp(value)
                    }}
                    disabled={isLoading}
                    required
                    autoFocus
                  />
                </div>
              </div>

              {error && <div className="auth-error">{error}</div>}

              <button
                type="submit"
                className="auth-button auth-button-primary"
                disabled={isLoading || otp.length !== 6}
              >
                {isLoading ? (
                  <>
                    <span className="auth-loading"></span>
                    验证中...
                  </>
                ) : (
                  '验证并登录'
                )}
              </button>

              <button
                type="button"
                className="auth-button auth-button-secondary"
                onClick={handleBack}
                disabled={isLoading}
              >
                重新发送
              </button>
            </form>

            <p className="auth-hint">
              验证码有效期为 1 小时
              <br />
              未收到？请检查垃圾邮件或重新发送
            </p>
          </>
        )}
      </div>
    </div>
  )
}
